import { Injectable } from '@angular/core';
import { LoggerService } from '@core/services/logger/logger.service';
import { DentureDetailsQuery } from '@modules/denture-details/state/denture-details.query';
import { DentureDetailsStore } from '@modules/denture-details/state/denture-details.store';
import { OrderQuery } from '@modules/order/state/order-query';
import { OrderStore } from '@modules/order/state/order-store';
import { ScanOptionsFacade } from '@modules/scan-options/scan-options.facade';
import { ScanOptionsQuery } from '@modules/scan-options/state/scan-options-query';
import { ScanOptionsStore } from '@modules/scan-options/state/scan-options-store';
import { TeethDiagramQuery } from '@modules/teeth-diagram/state/teeth-diagram-query';
import { AddRxService } from '@shared/services/add-rx.service';
import { HostPlatformService } from '@shared/services/host-platform.service';
import { OrthoModelICastService } from '@shared/services/ortho-model-icast.service';
import { ScanOptionsVisibilityService } from '@shared/services/scan-options-visibility.service';
import { SoftwareOptionsService } from '@shared/services/software-options.service';
import { VersionsService } from '@shared/services/versions.service';
import { ShellQuery } from '@shared/store/shell/shell-query';
import { combineLatest, merge, Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { IntuitiveFlowService } from '@shared/services/intuitive-flow.service';

@Injectable()
export class RxIntuitiveScanOptionsFacade extends ScanOptionsFacade {
	isReadOnly$: Observable<boolean> = this.shellQuery.getReadOnlyState('scanOptions');

	isNiriCaptureVisible$: Observable<boolean> = this.scanOptionsVisibilityService.isNiriCaptureVisible$;

	isPalatalFeedbackVisible$: Observable<boolean> = this.scanOptionsVisibilityService.isPalatalFeedbackVisible$;

	isOrthoModelIcastVisible$: Observable<boolean> = this.orthoModelICastService.isOrthoModelICastVisible$;

	isOrthoModelIcastChecked$: Observable<boolean> = this.orthoModelICastService.isOrthoModelICastToggleChecked$;

	isOrthoModelIcastDisabled$: Observable<boolean> = this.orthoModelICastService.isOrthoModelICastToggleDisabled('scanOptions');

	isSleeveConfirmationVisible$: Observable<boolean> = this.scanOptionsVisibilityService.isSleeveConfirmationVisible$;

	constructor(
		protected readonly store: ScanOptionsStore,
		protected readonly query: ScanOptionsQuery,
		protected readonly shellQuery: ShellQuery,
		protected readonly logger: LoggerService,
		protected readonly orderStore: OrderStore,
		protected readonly rxOrderQuery: OrderQuery,
		protected readonly dentureDetailsStore: DentureDetailsStore,
		protected readonly dentureDetailsQuery: DentureDetailsQuery,
		protected readonly teethDiagramQuery: TeethDiagramQuery,
		protected readonly softwareOptionsService: SoftwareOptionsService,
		protected readonly hostPlatformService: HostPlatformService,
		protected readonly versionService: VersionsService,
		protected readonly addRxService: AddRxService,
		private readonly orthoModelICastService: OrthoModelICastService,
		private readonly scanOptionsVisibilityService: ScanOptionsVisibilityService,
		private readonly intuitiveFlowService: IntuitiveFlowService
	) {
		super(
			store,
			query,
			shellQuery,
			logger,
			orderStore,
			rxOrderQuery,
			dentureDetailsStore,
			dentureDetailsQuery,
			teethDiagramQuery,
			softwareOptionsService,
			hostPlatformService,
			versionService,
			addRxService
		);
	}

	/** Any scan-option toggle visible and the RX is not globally read-only (same rule as former procedure-selected-page *ngIf). */
	isSectionVisible$: Observable<boolean> = combineLatest([
		this.isNiriCaptureVisible$,
		this.isPalatalFeedbackVisible$,
		this.isSleeveConfirmationVisible$,
		this.isOrthoModelIcastVisible$,
		this.isPreTreatmentVisible$,
		this.isDentureCopyScanVisible$,
		this.isEmergenceProfileVisible$,
		this.isMultiBiteVisible$,
		this.isRestoMultiBiteVisible$,
		this.shellQuery.isReadOnly$,
		this.intuitiveFlowService.isSummaryMode$
	]).pipe(
		map(
			([
				isNiriCaptureVisible,
				isPalatalFeedbackVisible,
				isSleeveConfirmationVisible,
				isOrthoModelIcastVisible,
				isPreTreatmentVisible,
				isDentureCopyScanVisible,
				isEmergenceProfileVisible,
				isMultiBiteVisible,
				isRestoMultiBiteVisible,
				isReadOnly,
				isSummaryMode
			]) =>
				[
					isNiriCaptureVisible,
					isPalatalFeedbackVisible,
					isSleeveConfirmationVisible,
					isOrthoModelIcastVisible,
					isPreTreatmentVisible,
					isDentureCopyScanVisible,
					isEmergenceProfileVisible,
					isMultiBiteVisible,
					isRestoMultiBiteVisible
				].some(isVisible => isVisible) &&
				!isReadOnly &&
				!isSummaryMode
		)
	);

	override loadPreTreatmentAndEmergenceProfile(): Observable<unknown> {
		const preTreatment$ = this.shellQuery.prePrepScan$.pipe(
			tap(prePrepScan => {
				if (prePrepScan === undefined) {
					const { isPreTreatmentUserSelected } = this.store.getValue();

					if (!isPreTreatmentUserSelected) {
						this.store.update({ isPreTreatmentChecked: false }); // set default
					}

					return;
				}

				this.store.update({ isPreTreatmentChecked: prePrepScan });
			})
		);

		const emergenceProfile$ = this.shellQuery.emergenceProfile$.pipe(
			tap(emergenceProfile => {
				if (emergenceProfile === undefined) {
					const { isEmergenceProfileUserSelected } = this.store.getValue();

					if (!isEmergenceProfileUserSelected) {
						this.store.update({ isEmergenceProfileChecked: false }); // set default
					}

					return;
				}

				this.store.update({ isEmergenceProfileChecked: emergenceProfile });
			})
		);

		return merge(preTreatment$, emergenceProfile$);
	}

	override handlePalatalFeedback(): Observable<unknown> {
		return combineLatest([
			this.shellQuery.palatalScanningFeedback$,
			this.isPalatalFeedbackVisible$,
			this.palatalFeedbackDefaultValue$
		]).pipe(
			tap(([rxPalatalScanningFeedback, isVisible, defaultPalatalScanningFeedback]) => {
				this.updatePalatalScanningFeedbackCheckState(!!isVisible, rxPalatalScanningFeedback, defaultPalatalScanningFeedback);
			})
		);
	}

	loadNiriInIntuitiveRx(): Observable<unknown> {
		return combineLatest([this.shellQuery.niriCaptureDefaultValue$, this.shellQuery.niriEnabled$, this.isNiriCaptureVisible$]).pipe(
			tap(([niriCaptureDefaultValue, niriEnabled, isNiriCaptureVisible]) => {
				if (niriEnabled === undefined) {
					const { isNiriUserSelected } = this.store.getValue();

					if (!isNiriUserSelected) {
						this.store.update({ isNiriCaptureChecked: niriCaptureDefaultValue && isNiriCaptureVisible }); // set default
					}

					return;
				}

				const isNiriChecked = typeof niriEnabled === 'boolean' ? niriEnabled : niriCaptureDefaultValue;

				this.store.update({ isNiriCaptureChecked: isNiriChecked && isNiriCaptureVisible });
			})
		);
	}
}
